import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { AuthProvider } from "@/contexts/AuthContext";
import { AppLayout } from "@/components/AppLayout";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { CookieConsent } from "@/components/CookieConsent";
import * as Sentry from "@sentry/react";

import LandingPage from "./pages/LandingPage";
import Login from "./pages/Login";
import SignUp from "./pages/SignUp";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import RoleDashboard from "./pages/RoleDashboard";
import StrataConfig from "./pages/StrataConfig";
import Hosts from "./pages/Hosts";
import AddHost from "./pages/AddHost";
import Bookings from "./pages/Bookings";
import Compliance from "./pages/Compliance";
import Units from "./pages/Units";
import AuditLog from "./pages/AuditLog";
import Reports from "./pages/Reports";
import Notifications from "./pages/Notifications";
import Disputes from "./pages/Disputes";
import Fines from "./pages/Fines";
import Amenities from "./pages/Amenities";
import AmenityManagement from "./pages/AmenityManagement";
import Requests from "./pages/Requests";
import Maintenance from "./pages/Maintenance";
import HostDashboard from "./pages/HostDashboard";
import IndividualDashboard from "./pages/IndividualDashboard";
import HostDocuments from "./pages/HostDocuments";
import Team from "./pages/Team";
import GuestPortal from "./pages/GuestPortal";
import AcceptInvite from "./pages/AcceptInvite";
import DebugSentry from "./pages/DebugSentry";
import NotFound from "./pages/NotFound";
import Pricing from "./pages/Pricing";
import AdminLogin from "./pages/AdminLogin";
import AdminPortal from "./pages/AdminPortal";

// Admin console — full platform management panel (system_admin only)
import { AdminLayout } from "./components/admin/AdminLayout";
import AdminDashboard from "./pages/admin/Dashboard";
import AdminUsers from "./pages/admin/Users";
import AdminUserDetail from "./pages/admin/UserDetail";
import AdminBuildings from "./pages/admin/Buildings";
import AdminBuildingDetail from "./pages/admin/BuildingDetail";
import AdminUnits from "./pages/admin/Units";
import AdminBookings from "./pages/admin/Bookings";
import AdminBookingDetail from "./pages/admin/BookingDetail";
import AdminCompliance from "./pages/admin/Compliance";
import AdminAuditLogs from "./pages/admin/AuditLogs";
import AdminSubscriptions from "./pages/admin/Subscriptions";
import AdminInvoices from "./pages/admin/Invoices";
import AdminMessages from "./pages/admin/Messages";
import AdminAnnouncements from "./pages/admin/Announcements";
import AdminSettings from "./pages/admin/Settings";
import AdminSettingsEmail from "./pages/admin/SettingsEmail";
import AdminSettingsFeatures from "./pages/admin/SettingsFeatures";
import Unauthorized from "./pages/admin/Unauthorized";
import Billing from "./pages/Billing";
import Profile from "./pages/Profile";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Messages from "./pages/Messages";
import MyInvitations from "./pages/MyInvitations";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 2, // 2 minutes
      retry: 1,
    },
    mutations: {
      // Consistent error format for all mutations
      onError: (error: Error) => {
        console.error("[Mutation Error]", error.message);
      },
    },
  },
});

/**
 * Fallback UI shown when Sentry.ErrorBoundary catches a render error.
 * Sentry will also show its user feedback dialog (showDialog prop).
 */
function SentryFallback({ resetError }: { resetError: () => void }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center space-y-4">
        <h1 className="text-2xl font-bold text-destructive">Something went wrong</h1>
        <p className="text-muted-foreground">
          An unexpected error occurred. It has been reported automatically.
        </p>
        <button
          onClick={resetError}
          className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          Try again
        </button>
      </div>
    </div>
  );
}

const App = () => (
  // Sentry error boundary (outermost) — reports uncaught render errors + shows dialog
  <Sentry.ErrorBoundary fallback={(props) => <SentryFallback resetError={props.resetError} />} showDialog>
    {/* Local error boundary — friendly fallback UI when Sentry is disabled */}
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider attribute="class" defaultTheme="light">
          <AuthProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <CookieConsent />
              <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                <Routes>
                  {/* Root — shows marketing landing page to everyone.
                      Authenticated users are redirected to /dashboard by LandingPage itself. */}
                  <Route path="/" element={<LandingPage />} />
                  {/* /landing is a backward-compat alias → redirect to / */}
                  <Route path="/landing" element={<Navigate to="/" replace />} />
                  {/* Public pages — no auth required */}
                  <Route path="/login" element={<Login />} />
                  <Route path="/signup" element={<SignUp />} />
                  {/* Unauthorized page */}
                  <Route path="/unauthorized" element={<Unauthorized />} />

                  {/* Admin login (public entry point for system_admin) */}
                  <Route path="/admin-login" element={<AdminLogin />} />

                  {/* Legacy impersonation portal — keep for backward compat */}
                  <Route path="/admin-portal" element={
                    <ProtectedRoute allowedRoles={["system_admin"]}>
                      <AdminPortal />
                    </ProtectedRoute>
                  } />

                  {/* ─── Admin Console (/admin/*) — system_admin only ─────────── */}
                  <Route
                    path="/admin"
                    element={
                      <ProtectedRoute allowedRoles={["system_admin"]}>
                        <AdminLayout />
                      </ProtectedRoute>
                    }
                  >
                    <Route index element={<Navigate to="/admin/dashboard" replace />} />
                    <Route path="dashboard" element={<AdminDashboard />} />
                    <Route path="users" element={<AdminUsers />} />
                    <Route path="users/:id" element={<AdminUserDetail />} />
                    <Route path="buildings" element={<AdminBuildings />} />
                    <Route path="buildings/:id" element={<AdminBuildingDetail />} />
                    <Route path="units" element={<AdminUnits />} />
                    <Route path="bookings" element={<AdminBookings />} />
                    <Route path="bookings/:id" element={<AdminBookingDetail />} />
                    <Route path="compliance" element={<AdminCompliance />} />
                    <Route path="audit-logs" element={<AdminAuditLogs />} />
                    <Route path="subscriptions" element={<AdminSubscriptions />} />
                    <Route path="invoices" element={<AdminInvoices />} />
                    <Route path="messages" element={<AdminMessages />} />
                    <Route path="announcements" element={<AdminAnnouncements />} />
                    <Route path="settings" element={<AdminSettings />} />
                    <Route path="settings/email" element={<AdminSettingsEmail />} />
                    <Route path="settings/features" element={<AdminSettingsFeatures />} />
                  </Route>
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  {/* Pricing is public (visible before sign-in too) */}
                  <Route path="/pricing" element={<Pricing />} />
                  {/* Legal pages — public, no auth required */}
                  <Route path="/terms" element={<TermsOfService />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route
                    path="/guest/:token"
                    element={
                      <ErrorBoundary>
                        <GuestPortal />
                      </ErrorBoundary>
                    }
                  />
                  <Route
                    path="/accept-invite"
                    element={
                      <ErrorBoundary>
                        <AcceptInvite />
                      </ErrorBoundary>
                    }
                  />

                  {/* App pages (with sidebar layout + auth protection) */}
                  <Route
                    element={
                      <ProtectedRoute>
                        <ErrorBoundary>
                          <AppLayout />
                        </ErrorBoundary>
                      </ProtectedRoute>
                    }
                  >
                    {/* /dashboard is the authenticated home — sidebar links point here */}
                    <Route path="/dashboard" element={<RoleDashboard />} />

                    {/* Individual-only */}
                    <Route path="/individual-dashboard" element={
                      <ProtectedRoute allowedRoles={["individual"]}>
                        <IndividualDashboard />
                      </ProtectedRoute>
                    } />

                    {/* Host-only */}
                    <Route path="/host-dashboard" element={
                      <ProtectedRoute allowedRoles={["host"]}>
                        <HostDashboard />
                      </ProtectedRoute>
                    } />
                    <Route path="/my-bookings" element={
                      <ProtectedRoute allowedRoles={["host", "individual"]}>
                        <Bookings />
                      </ProtectedRoute>
                    } />
                    <Route path="/my-documents" element={
                      <ProtectedRoute allowedRoles={["host", "individual"]}>
                        <HostDocuments />
                      </ProtectedRoute>
                    } />

                    {/* Strata admin + PM */}
                    <Route path="/units" element={
                      <ProtectedRoute allowedRoles={["strata_admin", "property_manager", "individual"]}>
                        <Units />
                      </ProtectedRoute>
                    } />
                    <Route path="/hosts" element={
                      <ProtectedRoute allowedRoles={["strata_admin", "property_manager"]}>
                        <Hosts />
                      </ProtectedRoute>
                    } />
                    <Route path="/hosts/add" element={
                      <ProtectedRoute allowedRoles={["strata_admin", "property_manager"]}>
                        <AddHost />
                      </ProtectedRoute>
                    } />
                    <Route path="/bookings" element={
                      <ProtectedRoute allowedRoles={["strata_admin", "property_manager", "individual"]}>
                        <Bookings />
                      </ProtectedRoute>
                    } />
                    <Route path="/compliance" element={
                      <ProtectedRoute allowedRoles={["strata_admin", "property_manager"]}>
                        <Compliance />
                      </ProtectedRoute>
                    } />
                    <Route path="/audit-log" element={
                      <ProtectedRoute allowedRoles={["strata_admin", "property_manager"]}>
                        <AuditLog />
                      </ProtectedRoute>
                    } />
                    <Route path="/reports" element={
                      <ProtectedRoute allowedRoles={["strata_admin", "property_manager"]}>
                        <Reports />
                      </ProtectedRoute>
                    } />
                    <Route path="/amenity-management" element={
                      <ProtectedRoute allowedRoles={["strata_admin", "property_manager"]}>
                        <AmenityManagement />
                      </ProtectedRoute>
                    } />

                    {/* Strata admin only */}
                    <Route path="/strata-config" element={
                      <ProtectedRoute allowedRoles={["strata_admin"]}>
                        <StrataConfig />
                      </ProtectedRoute>
                    } />
                    <Route path="/team" element={
                      <ProtectedRoute allowedRoles={["strata_admin"]}>
                        <Team />
                      </ProtectedRoute>
                    } />

                    {/* All authenticated roles */}
                    <Route path="/disputes" element={<Disputes />} />
                    <Route path="/my-fines" element={<Fines />} />
                    <Route path="/amenities" element={<Amenities />} />
                    <Route path="/requests" element={<Requests />} />
                    <Route path="/maintenance" element={<Maintenance />} />
                    <Route path="/notifications" element={<Notifications />} />
                    <Route path="/messages" element={<Messages />} />
                    <Route path="/my-invitations" element={<MyInvitations />} />
                    {/* Billing — subscription management (all auth'd roles) */}
                    <Route path="/account/billing" element={<Billing />} />
                    {/* User profile */}
                    <Route path="/profile" element={<Profile />} />
                  </Route>

                  {/* Debug / testing route — gated to system_admin only */}
                  <Route
                    path="/debug-sentry"
                    element={
                      <ProtectedRoute allowedRoles={["system_admin"]}>
                        <DebugSentry />
                      </ProtectedRoute>
                    }
                  />

                  <Route path="*" element={<NotFound />} />
                </Routes>
              </BrowserRouter>
            </TooltipProvider>
          </AuthProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </Sentry.ErrorBoundary>
);

export default App;
